/**
 * The credential register (wave b12 frontend item 9; wave CRED rulings 15, 16 and 18).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The register lists the credentials the caller may read (the operator's whole tenant; a
 * counterparty its own party's people on its own contracts), filtered by holder on the
 * server (holder kind, then the person or the supplier), each row with its status chip
 * (EXPIRED read off the validity date, never stored) and its evidence files. The QC lead
 * and the head (CREDENTIAL MANAGE in the shipped policy) record a credential and suspend,
 * revoke or restore one; an instrument's row opens its impact list, the inspections it
 * measured on. Every refusal is said inside its dialog in the server's words.
 */

import { useState } from "react";
import { Link, useOutletContext } from "react-router";

import { uploadAttachment } from "../api/attachments";
import { ApiError } from "../api/client";
import {
  credentialImpact,
  displayStatus,
  holderName,
  listCredentialTypes,
  listCredentials,
  recordCredential,
  setCredentialStatus,
  type Credential,
  type CredentialType,
  type HolderKind,
  type ImpactItem,
} from "../api/credentials";
import type { Me } from "../api/me";
import { listParties } from "../api/portfolio";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { HistoryButton } from "../components/HistoryDrawer";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { FilePicker } from "@/components/ui/file-picker";
import { ServerField } from "../lib/serverErrors";
import { ActionDialog, ToastBar } from "../nav/ActionDialog";
import { readColleagues } from "../nav/FrontDoorDialogs";
import { DownloadLink } from "./Inspections";
import { EmptyState, LoadingLine, SkeletonRows, StateChip, day, stamp } from "./parts";
import { useQuery } from "./useQuery";

/** The classes that hold CREDENTIAL MANAGE in the shipped policy (QC lead, the head). */
export function mayManageCredentials(me: Me | null | undefined): boolean {
  return me?.workflowClass === "qc_lead" || me?.workflowClass === "manager";
}

const KINDS: readonly HolderKind[] = ["PERSON", "SUPPLIER", "INSTRUMENT"];

export function CredentialStatusChip({ row }: { readonly row: Credential }): React.JSX.Element {
  const shown = displayStatus(row);
  return (
    <span
      data-testid="credential-status"
      data-status={shown}
      title={
        shown === "EXPIRED" ? strings.credentials.expiredTitle : (row.statusReason ?? undefined)
      }
    >
      <StateChip
        state={shown === "VALID" ? "CONFIRMED" : shown === "SUSPENDED" ? "OPEN" : "REJECTED"}
        label={strings.credentials.status[shown] ?? shown}
      />
    </span>
  );
}

export function CredentialsPage(): React.JSX.Element {
  const token = useToken();
  const outlet = useOutletContext<{ me?: Me | null } | undefined>();
  const manage = mayManageCredentials(outlet?.me);
  const copy = strings.credentials;
  const [kind, setKind] = useState<HolderKind | null>(null);
  const [holder, setHolder] = useState("");
  const [reload, setReload] = useState(0);
  const [recording, setRecording] = useState(false);
  const [acting, setActing] = useState<{
    row: Credential;
    status: "VALID" | "SUSPENDED" | "REVOKED";
  } | null>(null);
  const [impactOf, setImpactOf] = useState<Credential | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // The holder filter names a person or a supplier; an instrument is narrowed by its kind.
  const [holderKey, holderId] = holder.includes(":") ? holder.split(":", 2) : ["", ""];
  const register = useQuery(
    (signal) =>
      listCredentials(token, {
        holderKind: kind,
        holderPersonId: holderKey === "person" ? holderId : null,
        holderPartyId: holderKey === "party" ? holderId : null,
        signal,
      }),
    [token, kind, holder, reload],
  );
  // The holders the filter offers: every holder the kind's unfiltered rows name.
  const holders = useQuery(
    (signal) => listCredentials(token, { holderKind: kind, signal }),
    [token, kind, reload],
  );
  const holderOptions = new Map<string, string>();
  for (const row of holders.kind === "ready" ? holders.data : []) {
    if (row.holderPersonId !== null) {
      holderOptions.set(`person:${row.holderPersonId}`, holderName(row));
    } else if (row.holderPartyId !== null) {
      holderOptions.set(`party:${row.holderPartyId}`, holderName(row));
    }
  }
  const narrowed = kind !== null || holder !== "";

  const columns: RegisterColumn<Credential>[] = [
    { id: "type", label: copy.columnType, text: (row) => row.typeTitle, filter: true },
    {
      id: "holder",
      label: copy.columnHolder,
      text: (row) => holderName(row),
      render: (row) => (
        <span>
          {holderName(row)}{" "}
          <span className="text-xs text-muted-foreground">({copy.kind[row.holderKind]})</span>
        </span>
      ),
    },
    { id: "reference", label: copy.columnReference, text: (row) => row.reference },
    {
      id: "issued",
      label: copy.columnIssued,
      text: (row) => day(row.issuedOn),
      sortValue: (row) => row.issuedOn,
    },
    {
      id: "valid",
      label: copy.columnValidUntil,
      text: (row) => (row.validUntil === null ? copy.noExpiry : day(row.validUntil)),
      sortValue: (row) => row.validUntil ?? "9999-12-31",
    },
    {
      id: "status",
      label: copy.columnStatus,
      text: (row) => copy.status[displayStatus(row)] ?? displayStatus(row),
      filter: true,
      render: (row) => <CredentialStatusChip row={row} />,
    },
    {
      id: "evidence",
      label: copy.columnEvidence,
      text: (row) => String(row.evidenceAttachmentIds.length),
      render: (row) =>
        row.evidenceAttachmentIds.length === 0 ? (
          <span className="text-muted-foreground">{copy.noEvidence}</span>
        ) : (
          <span className="flex flex-col gap-1" data-testid="credential-evidence">
            {row.evidenceAttachmentIds.map((id, index) => (
              <DownloadLink key={id} attachmentId={id} filename={copy.evidenceFile(index + 1)} />
            ))}
          </span>
        ),
    },
    {
      id: "acts",
      label: strings.nav.actions,
      text: () => "",
      render: (row) => (
        <span className="q-actions-row">
          <HistoryButton objectType="CREDENTIAL" objectId={row.id} recordLabel={row.reference} />
          {row.holderKind === "INSTRUMENT" && (
            <button
              type="button"
              className="q-button q-button--quiet"
              data-testid="credential-impact"
              onClick={() => {
                setImpactOf(row);
              }}
            >
              {copy.impact}
            </button>
          )}
          {manage && row.status === "VALID" && (
            <>
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="credential-suspend"
                onClick={() => {
                  setActing({ row, status: "SUSPENDED" });
                }}
              >
                {copy.suspend}
              </button>
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="credential-revoke"
                onClick={() => {
                  setActing({ row, status: "REVOKED" });
                }}
              >
                {copy.revoke}
              </button>
            </>
          )}
          {manage && row.status === "SUSPENDED" && (
            <>
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="credential-restore"
                onClick={() => {
                  setActing({ row, status: "VALID" });
                }}
              >
                {copy.restore}
              </button>
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="credential-revoke"
                onClick={() => {
                  setActing({ row, status: "REVOKED" });
                }}
              >
                {copy.revoke}
              </button>
            </>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <h2 className="q-shell__heading">{copy.heading}</h2>
      <p className="q-shell__lede">{copy.lede}</p>
      {toast !== null && (
        <ToastBar
          message={toast}
          onDone={() => {
            setToast(null);
          }}
        />
      )}
      <div className="q-actions-row q-gap-top" role="group" aria-label={copy.kindFilterLabel}>
        {[null, ...KINDS].map((one) => (
          <button
            key={one ?? "all"}
            type="button"
            aria-pressed={kind === one}
            data-testid={`credentials-kind-${(one ?? "all").toLowerCase()}`}
            className={`q-button${kind === one ? " q-mini--primary" : " q-button--quiet"}`}
            onClick={() => {
              setKind(one);
              setHolder("");
            }}
          >
            {one === null ? copy.kindAll : copy.kind[one]}
          </button>
        ))}
        {manage && (
          <button
            type="button"
            className="q-button q-mini--primary"
            data-testid="credential-record"
            onClick={() => {
              setRecording(true);
            }}
          >
            {copy.record}
          </button>
        )}
      </div>
      {kind !== "INSTRUMENT" && holderOptions.size > 0 && (
        <div className="q-field" style={{ maxWidth: "24rem" }}>
          <label className="q-field__label" htmlFor="credentials-holder">
            {copy.holderFilterLabel}
          </label>
          <select
            id="credentials-holder"
            className="q-input"
            data-testid="credentials-holder"
            value={holder}
            onChange={(event) => {
              setHolder(event.target.value);
            }}
          >
            <option value="">{copy.holderAll}</option>
            {[...holderOptions.entries()]
              .sort((a, b) => a[1].localeCompare(b[1]))
              .map(([key, name]) => (
                <option key={key} value={key}>
                  {name}
                </option>
              ))}
          </select>
        </div>
      )}
      <div className="q-gap-top">
        {register.kind === "loading" && <SkeletonRows rows={5} columns={7} />}
        {(register.kind === "failed" || register.kind === "absence") && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
        {register.kind === "ready" &&
          (register.data.length === 0 ? (
            narrowed ? (
              <EmptyState title={copy.emptyFilteredTitle} body={copy.emptyFilteredBody} />
            ) : (
              <EmptyState title={copy.emptyTitle} body={copy.emptyBody} />
            )
          ) : (
            <DataTable
              data={register.data}
              columns={columns}
              caption={copy.caption}
              testId="credentials"
              getRowId={(row) => row.id}
              getRowTestId={() => "credential-row"}
              pageSize={25}
            />
          ))}
      </div>
      {recording && (
        <RecordCredentialDialog
          onClose={() => {
            setRecording(false);
          }}
          onDone={(reference) => {
            setRecording(false);
            setToast(copy.recorded(reference));
            setReload((one) => one + 1);
          }}
        />
      )}
      {acting !== null && (
        <CredentialStatusDialog
          row={acting.row}
          status={acting.status}
          onClose={() => {
            setActing(null);
          }}
          onDone={(message) => {
            setActing(null);
            setToast(message);
            setReload((one) => one + 1);
          }}
        />
      )}
      {impactOf !== null && (
        <ImpactDialog
          row={impactOf}
          onClose={() => {
            setImpactOf(null);
          }}
        />
      )}
    </>
  );
}

function RecordCredentialDialog({
  onClose,
  onDone,
}: {
  readonly onClose: () => void;
  readonly onDone: (reference: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.credentials;
  const types = useQuery((signal) => listCredentialTypes(token, signal), [token]);
  const colleagues = useQuery((signal) => readColleagues(token, signal), [token]);
  const parties = useQuery((signal) => listParties(token, signal), [token]);
  const [typeCode, setTypeCode] = useState("");
  const [personId, setPersonId] = useState("");
  const [partyId, setPartyId] = useState("");
  const [tag, setTag] = useState("");
  const [reference, setReference] = useState("");
  const [issuedOn, setIssuedOn] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [issuer, setIssuer] = useState("");
  const [evidence, setEvidence] = useState<readonly File[]>([]);
  const [uploaded] = useState(() => new Map<File, string>());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const type: CredentialType | undefined =
    types.kind === "ready" ? types.data.find((one) => one.code === typeCode) : undefined;
  const holderReady =
    type === undefined
      ? false
      : type.holderKind === "PERSON"
        ? personId !== ""
        : type.holderKind === "SUPPLIER"
          ? partyId !== ""
          : tag.trim() !== "";
  const ready =
    type !== undefined &&
    holderReady &&
    reference.trim() !== "" &&
    issuedOn !== "" &&
    (!type.validityRequired || validUntil !== "") &&
    (!type.requiresEvidence || evidence.length > 0);

  async function send(): Promise<void> {
    if (type === undefined) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const ids: string[] = [];
      for (const file of evidence) {
        let id = uploaded.get(file);
        if (id === undefined) {
          id = await uploadAttachment(token, file);
          uploaded.set(file, id);
        }
        ids.push(id);
      }
      const saved = await recordCredential(token, {
        type_code: type.code,
        holder_person_id: type.holderKind === "PERSON" ? personId : null,
        holder_party_id: type.holderKind === "SUPPLIER" ? partyId : null,
        instrument_tag: type.holderKind === "INSTRUMENT" ? tag.trim() : null,
        reference: reference.trim(),
        issued_on: issuedOn,
        valid_until: validUntil === "" ? null : validUntil,
        issuer: issuer.trim() === "" ? null : issuer.trim(),
        evidence_attachment_ids: ids,
      });
      onDone(saved.reference);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog open wide title={copy.recordHeading} onClose={onClose} error={error}>
      <div className="q-field">
        <label className="q-field__label" htmlFor="credential-type">
          {copy.typeLabel}
        </label>
        <ServerField field="type_code">
          <select
            id="credential-type"
            className="q-input"
            data-testid="credential-type"
            value={typeCode}
            onChange={(event) => {
              setTypeCode(event.target.value);
            }}
          >
            <option value="">{copy.typeChoose}</option>
            {(types.kind === "ready" ? types.data : []).map((one) => (
              <option key={one.code} value={one.code}>
                {one.title} ({copy.kind[one.holderKind]})
              </option>
            ))}
          </select>
        </ServerField>
      </div>
      {type?.holderKind === "PERSON" && (
        <div className="q-field">
          <label className="q-field__label" htmlFor="credential-person">
            {copy.personLabel}
          </label>
          <ServerField field="holder_person_id">
            <select
              id="credential-person"
              className="q-input"
              data-testid="credential-person"
              value={personId}
              onChange={(event) => {
                setPersonId(event.target.value);
              }}
            >
              <option value="">{copy.personChoose}</option>
              {(colleagues.kind === "ready" ? colleagues.data.people : []).map((one) => (
                <option key={one.id} value={one.id}>
                  {one.label}
                </option>
              ))}
            </select>
          </ServerField>
          <p className="q-note">{copy.personHint}</p>
        </div>
      )}
      {type?.holderKind === "SUPPLIER" && (
        <div className="q-field">
          <label className="q-field__label" htmlFor="credential-party">
            {copy.partyLabel}
          </label>
          <ServerField field="holder_party_id">
            <select
              id="credential-party"
              className="q-input"
              data-testid="credential-party"
              value={partyId}
              onChange={(event) => {
                setPartyId(event.target.value);
              }}
            >
              <option value="">{copy.partyChoose}</option>
              {(parties.kind === "ready" ? parties.data : []).map((one) => (
                <option key={one.id} value={one.id}>
                  {one.name}
                </option>
              ))}
            </select>
          </ServerField>
        </div>
      )}
      {type?.holderKind === "INSTRUMENT" && (
        <div className="q-field">
          <label className="q-field__label" htmlFor="credential-tag">
            {copy.instrumentLabel}
          </label>
          <ServerField field="instrument_tag">
            <input
              id="credential-tag"
              className="q-input"
              data-testid="credential-tag"
              value={tag}
              onChange={(event) => {
                setTag(event.target.value);
              }}
            />
          </ServerField>
        </div>
      )}
      <div className="q-field">
        <label className="q-field__label" htmlFor="credential-reference">
          {copy.referenceLabel}
        </label>
        <ServerField field="reference">
          <input
            id="credential-reference"
            className="q-input"
            data-testid="credential-reference"
            value={reference}
            onChange={(event) => {
              setReference(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-form-grid">
        <div className="q-field">
          <label className="q-field__label" htmlFor="credential-issued">
            {copy.issuedLabel}
          </label>
          <ServerField field="issued_on">
            <input
              id="credential-issued"
              className="q-input"
              type="date"
              data-testid="credential-issued"
              value={issuedOn}
              onChange={(event) => {
                setIssuedOn(event.target.value);
              }}
            />
          </ServerField>
        </div>
        <div className="q-field">
          <label className="q-field__label" htmlFor="credential-valid-until">
            {copy.validUntilLabel}
          </label>
          <ServerField field="valid_until">
            <input
              id="credential-valid-until"
              className="q-input"
              type="date"
              data-testid="credential-valid-until"
              value={validUntil}
              onChange={(event) => {
                setValidUntil(event.target.value);
              }}
            />
          </ServerField>
          <p className="q-note">
            {type?.validityRequired === true ? copy.validUntilRequired : copy.validUntilOptional}
          </p>
        </div>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="credential-issuer">
          {copy.issuerLabel}
        </label>
        <ServerField field="issuer">
          <input
            id="credential-issuer"
            className="q-input"
            data-testid="credential-issuer"
            value={issuer}
            onChange={(event) => {
              setIssuer(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-field">
        <span className="q-field__label">{copy.evidenceLabel}</span>
        <ServerField field="evidence_attachment_ids">
          <FilePicker
            files={evidence}
            onChange={setEvidence}
            label={copy.evidenceLabel}
            testId="credential-evidence-files"
            emptyText={copy.evidenceEmpty}
          />
        </ServerField>
        {type?.requiresEvidence === true && <p className="q-note">{copy.evidenceRequired}</p>}
      </div>
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="credential-record-send"
          disabled={busy || !ready}
          onClick={() => {
            void send();
          }}
        >
          {copy.recordSend}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

function CredentialStatusDialog({
  row,
  status,
  onClose,
  onDone,
}: {
  readonly row: Credential;
  readonly status: "VALID" | "SUSPENDED" | "REVOKED";
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.credentials;
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const act =
    status === "VALID" ? copy.restore : status === "SUSPENDED" ? copy.suspend : copy.revoke;
  const needsReason = status !== "VALID";
  const ready = !needsReason || reason.trim() !== "";

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const saved = await setCredentialStatus(token, row.id, {
        status,
        reason: reason.trim() === "" ? null : reason.trim(),
      });
      onDone(copy.statusDone(saved.reference, copy.status[displayStatus(saved)] ?? saved.status));
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      title={copy.statusHeading(act, row.reference)}
      onClose={onClose}
      error={error}
    >
      <p className="q-shell__lede">{copy.statusLede[status]}</p>
      <div className="q-field">
        <label className="q-field__label" htmlFor="credential-reason">
          {needsReason ? copy.reasonLabel : copy.reasonOptional}
        </label>
        <ServerField field="reason">
          <textarea
            id="credential-reason"
            className="q-textarea"
            data-testid="credential-reason"
            aria-required={needsReason}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
        </ServerField>
        {!ready && <p className="q-note">{copy.reasonNeeded}</p>}
      </div>
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="credential-status-send"
          disabled={busy || !ready}
          onClick={() => {
            void send();
          }}
        >
          {copy.statusSend(act)}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

function ImpactDialog({
  row,
  onClose,
}: {
  readonly row: Credential;
  readonly onClose: () => void;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.credentials;
  const impact = useQuery((signal) => credentialImpact(token, row.id, signal), [token, row.id]);
  const columns: RegisterColumn<ImpactItem>[] = [
    {
      id: "record",
      label: copy.impactColumnRecord,
      text: (one) => `${one.humanId} ${one.title}`,
      render: (one) => (
        <Link className="q-link" to={`/inspections/${one.verificationId}`}>
          {one.humanId}
        </Link>
      ),
    },
    { id: "title", label: copy.columnType, text: (one) => one.title },
    {
      id: "state",
      label: copy.impactColumnState,
      text: (one) => one.workflowState,
      render: (one) => <StateChip state={one.workflowState} />,
    },
    {
      id: "count",
      label: copy.impactColumnCount,
      text: (one) => String(one.measurementCount),
      sortValue: (one) => one.measurementCount,
    },
    {
      id: "first",
      label: copy.impactColumnFirst,
      text: (one) => stamp(one.firstMeasuredAt),
      sortValue: (one) => one.firstMeasuredAt,
    },
  ];
  return (
    <ActionDialog
      open
      wide
      title={copy.impactHeading(holderName(row))}
      onClose={onClose}
      error={impact.kind === "failed" ? impact.error : undefined}
    >
      <p className="q-shell__lede">{copy.impactLede}</p>
      {impact.kind === "loading" && <LoadingLine label={strings.common.loading} />}
      {impact.kind === "ready" &&
        (impact.data.items.length === 0 ? (
          <p className="q-note" data-testid="impact-empty">
            {copy.impactEmpty}
          </p>
        ) : (
          <DataTable
            data={impact.data.items}
            columns={columns}
            caption={copy.impactCaption}
            testId="impact"
            getRowId={(one) => one.verificationId}
            getRowTestId={() => "impact-row"}
          />
        ))}
    </ActionDialog>
  );
}
